import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Avatar,
  Fab,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Box,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useMsal } from "@azure/msal-react";
import SelectCluster from "../components/selectCluster";
import FilterListIcon from "@mui/icons-material/FilterList";
import Loading from "../components/loading";
import TimeFormater from "../components/dialogs/timeFormater";
import { useDebounce } from "../components/hooks/useDebounce";
import { useSecrets } from "../components/hooks/useSecrets";
import { useApiHeaders } from "../components/hooks/useApiHeaders";
import { getTagValue } from "../components/utils/tagHelpers";
import { config } from "../components/config";
import useAccessToken from "../auth/accessPermission";
import useUserAccount from "../components/hooks/userAccountInfo";

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minHeight: "100vh",
};

const tableContainerStyle = {
  width: "80%",
};

function SecretList() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialCluster =
    searchParams.get("selectedCluster") || "kuda-devops-eks";

  const [secrets, setSecrets] = useState([]);
  const { instance } = useMsal();
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);

  const [selectedCluster, setSelectedCluster] = useState(initialCluster);
  const [vaultId, setVaultId] = useState(
    config.secretStore[initialCluster]?.[1]
  );
  const [platformName, setPlatformName] = useState(
    config.secretStore[initialCluster]?.[0]
  );

  // hooks
  const accessToken = useAccessToken([config.apiAccess]);
  const { project, userRole } = useUserAccount(instance);
  const { loading, fetchSecrets } = useSecrets();

  const headers = useApiHeaders(
    accessToken,
    userRole,
    project,
    platformName,
    vaultId
  );

  // unified loading check
  const isDataReady =
    !!accessToken &&
    !!userRole &&
    !!platformName &&
    !!vaultId &&
    !loading;

  const handleNameCellClick = (params) => {
    const currentCluster = selectedCluster;
    const currentConfig = config.secretStore[currentCluster];
    const currentPlatformName = currentConfig ? currentConfig[0] : null;
    const currentVaultId = currentConfig ? currentConfig[1] : null;

    if (!currentCluster || !currentPlatformName || !currentVaultId) {
      alert(
        "Error: Missing cluster configuration. Please select a cluster and try again."
      );
      return;
    }

    navigate(
      `/view-secret/${params.row.name}?platformName=${currentPlatformName}&selectedCluster=${currentCluster}&vaultName=${currentVaultId}`
    );
  };

  // table columns
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 0.2,
        minWidth: 80,
        renderHeader: () => <strong style={{ color: "#616161" }}>ID</strong>,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        minWidth: 200,
        renderHeader: () => <strong style={{ color: "#616161" }}>Name</strong>,
        renderCell: (params) => (
          <div
            onClick={() => handleNameCellClick(params)}
            style={{ cursor: "pointer" }}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: "Environment",
        headerName: "Environment",
        flex: 0.9,
        minWidth: 120,
        renderHeader: () => (
          <strong style={{ color: "#616161" }}>Environment</strong>
        ),
        valueGetter: (params) => getTagValue(params.row.tags, "Environment"),
      },
      {
        field: "Project",
        headerName: "Team",
        flex: 1,
        minWidth: 100,
        renderHeader: () => <strong style={{ color: "#616161" }}>Team</strong>,
        valueGetter: (params) => getTagValue(params.row.tags, "Project"),
      },
      {
        field: "updated_on",
        headerName: "Last Updated",
        flex: 1,
        minWidth: 150,
        renderHeader: () => (
          <strong style={{ color: "#616161" }}>Last Updated</strong>
        ),
        renderCell: (params) => (
          <TimeFormater timestamp={params.row.updated_on} />
        ),
      },
    ],
    [selectedCluster]
  );

  // filtered rows
  const filteredRows = useMemo(() => {
    if (!debouncedSearchText) return secrets;

    return secrets.filter((row) =>
      columns.some((column) => {
        const value = row[column.field];
        return (
          value &&
          value.toString().toLowerCase().includes(debouncedSearchText.toLowerCase())
        );
      })
    );
  }, [secrets, debouncedSearchText, columns]);

  // process project string for avatar + display
  const processProjectString = useCallback((projectStr) => {
    if (!projectStr || typeof projectStr !== "string")
      return { initials: "", displayName: "" };

    const stopWords = ["and", "or", "the", "of", "in", "at", "to", "for", "with"];

    const allWords = projectStr
      .split("-")
      .map((word) => word.trim())
      .filter((word) => word.length > 0);

    const initials = allWords
      .filter((word) => !stopWords.includes(word.toLowerCase()))
      .map((word) => word[0].toUpperCase())
      .join("");

    const displayName = allWords
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return { initials, displayName };
  }, []);

  const handleEditClick = useCallback(() => {
    navigate("/create-secret");
  }, [navigate]);

  const handleClusterChange = useCallback(
    (cluster) => {
      setSelectedCluster(cluster);
      setPlatformName(config.secretStore[cluster]?.[0]);
      setVaultId(config.secretStore[cluster]?.[1]);

      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("selectedCluster", cluster);
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    },
    [navigate, location]
  );

  const handleSearch = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  const capitalizeFirstLetter = useCallback(
    (str) => str.charAt(0).toUpperCase() + str.slice(1),
    []
  );

  const { initials, displayName } = useMemo(
    () => processProjectString(project),
    [project, processProjectString]
  );

  // fetch secrets after login ready
  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentAccount = instance.getActiveAccount();
        if (
          currentAccount &&
          accessToken &&
          userRole &&
          platformName &&
          vaultId
        ) {
          const secretsData = await fetchSecrets(platformName, headers);
          setSecrets(secretsData);
        }
      } catch (error) {
        console.error("Error fetching secrets:", error);
      }
    };

    if (platformName && vaultId) {
      fetchData();
    }
  }, [
    instance,
    userRole,
    accessToken,
    selectedCluster,
    platformName,
    headers,
    fetchSecrets,
    vaultId,
  ]);

  // loading skeleton
  if (!isDataReady) {
    return (
      <div style={containerStyle}>
        <div style={tableContainerStyle}>
          <Loading
            open
            type="skeleton"
            variant="table"
            rows={10}
            width="100%"
            height="85vh"
            position="center"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={tableContainerStyle}>
        <Typography
          variant="h3"
          gutterBottom
          style={{ textAlign: "center", color: "#1976D2" }}
        >
          {capitalizeFirstLetter(selectedCluster)} Cluster Secrets
        </Typography>

        <div
          style={{
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {project && (
            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 5, display: "flex", alignItems: "center" }}
            >
              <Avatar
                sx={{
                  bgcolor: "secondary.main",
                  width: 60,
                  height: 60,
                  fontSize: 30,
                  borderRadius: "6%",
                  fontWeight: "normal",
                }}
              >
                {initials}
              </Avatar>
              <Typography variant="h5">{displayName}</Typography>
            </Stack>
          )}

          <SelectCluster
            onClusterChange={handleClusterChange}
            style={{ width: "30ch", mr: 10, mt: 5 }}
            label="Current Cluster"
            defaultCluster={selectedCluster}
            showAll={true}
          />

          <TextField
            variant="standard"
            size="small"
            sx={{ mt: 5 }}
            placeholder="Filter by name"
            value={searchText}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title="Create new secret" arrow>
            <Fab
              color="primary"
              size="small"
              onClick={handleEditClick}
              sx={{ mr: 10, mt: 5 }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        </div>
      </div>

      <div style={{ width: "90%" }}>
        {filteredRows.length > 0 ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", overflowX: "auto" }}
          >
            <div style={{ width: "100%", height: "75vh" }}>
              <DataGrid
                rows={filteredRows}
                columns={columns}
                autoPageSize
                checkboxSelection
              />
            </div>
          </Box>
        ) : (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <p>No secrets have been created.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SecretList;